// infrastructure/mappers/billingIntentMapper.js

/**
 * Between `billingIntents` documents and the BillingIntent aggregate.
 *
 * `isBlocking` IS THE STATE, and the only field anything queries. `releaseTimestamp` is audit —
 * deliberately NOT paired with a start bound, because a start/end pair reads as an effective period
 * and someone would eventually resolve it against a service date. The block began at
 * `createTimestamp`, which the platform stamps and never moves.
 *
 * `isDeleted` is neither. It keeps the platform meaning — a record entered in error — and readers
 * filter it for the same reason they filter it anywhere. Using it as the on/off switch would say a
 * released block never happened, and the next block would overwrite the evidence that it did.
 *
 * Scoping keys are the platform-standard bare `client` and `clinic`, for the same reason as
 * `clientCoverages`: the Firestore rules helpers read those exact names. `coverageId` keeps its
 * suffix — it is a plain foreign key, not something a rules helper looks at.
 *
 * `blockedBy` and `releasedBy` are written EXPLICITLY rather than read off GenericDB's `updateBy`,
 * which every later write moves. Who withheld a payer and who let it resume are exactly what an
 * audit asks, and neither can rest on a value that shifts. `createTimestamp` needs no such
 * treatment: it is set once and never touched again.
 */

/**
 * @param {Object} doc - a billingIntents document
 * @returns {import('../../domain/model/BillingIntent.types').BillingIntent}
 */
export function toBillingIntent(doc) {
  return {
    id: doc.id,
    clientId: doc.client ?? null,
    clinicId: doc.clinic ?? null,
    coverageId: doc.coverageId ?? null,
    reasonType: doc.reasonType ?? null,
    isBlocking: doc.isBlocking === true,
    blockedBy: doc.blockedBy ?? null,
    releaseTimestamp: doc.releaseTimestamp ?? null,
    releasedBy: doc.releasedBy ?? null,
    isDeleted: doc.isDeleted === true,
  };
} // toBillingIntent()

/**
 * The fields written when a block is taken. Always writes it OPEN — releasing is a separate update,
 * so re-writing this over a released record would reopen it. `blockedBy` is supplied by the
 * adapter, which owns the current user; the domain object carries no identity of its own.
 *
 * @param {import('../../domain/model/BillingIntent.types').BillingIntent} billingIntent
 * @returns {Object} document fields, without the id
 */
export function toDocument(billingIntent) {
  return {
    client: billingIntent.clientId,
    clinic: billingIntent.clinicId,
    coverageId: billingIntent.coverageId,
    reasonType: billingIntent.reasonType ?? null,
    isBlocking: true,
    releaseTimestamp: null,
    releasedBy: null,
    isDeleted: false,
  };
} // toDocument()
